import { computed, makeObservable, observable, runInAction } from 'mobx';

import { inject, injectable } from '@servicetitan/react-ioc';

import { FeatureType, MobileContextApi } from '@fleet-pro/mobile-webview-api';

import { RequestManager } from '../../../stores/request-manager';
import { isAxiosCancellationError } from '../../../utils/is-cancellation-error';
import { RequestState } from '../../../utils/request-states';

@injectable()
export class FeatureSetsDataStore extends RequestManager {
    @observable availableFeatures: Set<FeatureType> = new Set();

    readonly getFeatureSetsKey = 'getFeatureSetsKey';

    constructor(@inject(MobileContextApi) private readonly mobileContextApi: MobileContextApi) {
        super();
        makeObservable(this);
    }

    cancelFetchFeatureSets = () => {
        this.cancelRequest(this.getFeatureSetsKey);
    };

    @computed get featureSetsRequestState() {
        return this.getRequestState(this.getFeatureSetsKey);
    }

    fetchFeatureSets = async () => {
        this.cancelRequest(this.getFeatureSetsKey);
        this.resetAbortController(this.getFeatureSetsKey);

        this.setRequestState(this.getFeatureSetsKey, RequestState.Loading);

        try {
            const response = await this.mobileContextApi.client.getAvailableFeatures({
                signal: this.getAbortController(this.getFeatureSetsKey)?.signal,
            });

            runInAction(() => {
                const features = response.data?.map(feature => feature.type) ?? [];

                this.availableFeatures = new Set(features);
            });

            this.setRequestState(this.getFeatureSetsKey, RequestState.Success);
        } catch (error) {
            if (isAxiosCancellationError(error)) {
                this.setRequestState(this.getFeatureSetsKey, RequestState.Idle);
            } else {
                this.setRequestState(this.getFeatureSetsKey, RequestState.Error);
            }

            throw error;
        }
    };
}
